import {
  discountPoliciesSchema,
  type DiscountPolicyFormValue,
  type DiscountReasonCodeFormValue,
} from "./discountPolicies.helpers";
import {
  discountWorkflowsSchema,
  type DiscountWorkflowFormValue,
} from "./discountWorkflows.helpers";
import type {
  DiscountPolicy,
  DiscountWorkflow,
  FinancialProfilesPayload,
  FinancialProfilesResponse,
} from "@/lib/api/settings";

export const FINANCIAL_PROFILES_QUERY_KEY = ["settings", "financial-profiles"] as const;

export const ensureTimestamp = (value: number | null | undefined): number => {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return value;
  }
  return Date.now();
};

export const normalizePolicy = (policy: DiscountPolicy): DiscountPolicyFormValue => ({
  id: policy.id,
  name: policy.name,
  code: policy.code,
  type: policy.type === "amount" ? "amount" : "percentage",
  value: typeof policy.value === "number" ? policy.value : 0,
  maxStack: typeof policy.maxStack === "number" ? policy.maxStack : null,
  stackGroup: policy.stackGroup ?? null,
  allowStacking: Boolean(policy.allowStacking),
  approvalRequired: Boolean(policy.approvalRequired),
  reasonCodes: Array.isArray(policy.reasonCodes)
    ? policy.reasonCodes.map((reason) => ({
        code: reason.code,
        label: reason.label,
        requiresNote: Boolean(reason.requiresNote),
      }))
    : [],
  createdAt: ensureTimestamp(policy.createdAt),
  updatedAt: ensureTimestamp(policy.updatedAt),
});

export const normalizeWorkflow = (workflow: DiscountWorkflow): DiscountWorkflowFormValue => ({
  id: workflow.id,
  name: workflow.name,
  thresholdCents: typeof workflow.thresholdCents === "number" ? workflow.thresholdCents : 0,
  approverRoles:
    Array.isArray(workflow.approverRoles) && workflow.approverRoles.length > 0
      ? [...workflow.approverRoles]
      : ["manager"],
  escalationEmails: Array.isArray(workflow.escalationEmails)
    ? workflow.escalationEmails.filter((email) => typeof email === "string" && email.trim().length > 0)
    : [],
  requireReasonCode: workflow.requireReasonCode ?? true,
  createdAt: ensureTimestamp(workflow.createdAt),
  updatedAt: ensureTimestamp(workflow.updatedAt),
});

export const clonePolicies = (policies: DiscountPolicyFormValue[]): DiscountPolicyFormValue[] =>
  policies.map((policy) => ({
    ...policy,
    reasonCodes: policy.reasonCodes.map((reason) => ({ ...reason })),
  }));

export const cloneWorkflows = (workflows: DiscountWorkflowFormValue[]): DiscountWorkflowFormValue[] =>
  workflows.map((workflow) => ({
    ...workflow,
    approverRoles: [...workflow.approverRoles],
    escalationEmails: [...workflow.escalationEmails],
  }));

export const serializePolicy = (policy: DiscountPolicyFormValue): DiscountPolicy => ({
  id: policy.id.trim(),
  name: policy.name.trim(),
  code: policy.code.trim(),
  type: policy.type,
  value: Math.round(policy.value),
  maxStack: typeof policy.maxStack === "number" ? Math.round(policy.maxStack) : null,
  stackGroup: policy.stackGroup && policy.stackGroup.trim().length > 0 ? policy.stackGroup.trim() : null,
  allowStacking: policy.allowStacking,
  approvalRequired: policy.approvalRequired,
  reasonCodes: policy.reasonCodes.map((reason) => ({
    code: reason.code.trim(),
    label: reason.label.trim(),
    requiresNote: reason.requiresNote,
  })),
  createdAt: ensureTimestamp(policy.createdAt),
  updatedAt: ensureTimestamp(policy.updatedAt),
});

export const serializeWorkflow = (workflow: DiscountWorkflowFormValue): DiscountWorkflow => ({
  id: workflow.id.trim(),
  name: workflow.name.trim(),
  thresholdCents: Math.round(workflow.thresholdCents),
  approverRoles: Array.from(new Set(workflow.approverRoles)),
  escalationEmails: workflow.escalationEmails
    .map((email) => email.trim())
    .filter((email) => email.length > 0),
  requireReasonCode: workflow.requireReasonCode,
  createdAt: ensureTimestamp(workflow.createdAt),
  updatedAt: ensureTimestamp(workflow.updatedAt),
});

export const buildFinancialProfilesPayload = (
  policies: DiscountPolicyFormValue[],
  workflows: DiscountWorkflowFormValue[],
  taxProfiles: FinancialProfilesResponse["taxProfiles"],
  tipProfiles: FinancialProfilesResponse["tipProfiles"],
): FinancialProfilesPayload => ({
  discountPolicies: policies.map(serializePolicy),
  discountWorkflows: workflows.map(serializeWorkflow),
  taxProfiles,
  tipProfiles,
});

export type {
  DiscountPolicyFormValue,
  DiscountReasonCodeFormValue,
  DiscountWorkflowFormValue,
  FinancialProfilesPayload,
  FinancialProfilesResponse,
};

export {
  discountPoliciesSchema,
  discountWorkflowsSchema,
};
